export interface AfsUserChip {
  id?: number;
  slug: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export type AppointmentAfsRole = 'creator' | 'invited';

export function normalizeSlug(slug: string | null | undefined): string {
  return (slug ?? '').toString().trim();
}

export function getAppointmentCreator(
  creatorSlug: string | null | undefined,
  afsParticipants: AfsUserChip[] = [],
  organiserFallback?: AfsUserChip | null
): AfsUserChip | null {
  const normalizedCreatorSlug = normalizeSlug(creatorSlug);
  if (!normalizedCreatorSlug) {
    return organiserFallback ?? null;
  }

  const fromList = afsParticipants.find(
    participant => normalizeSlug(participant.slug) === normalizedCreatorSlug
  );
  if (fromList) {
    return fromList;
  }

  if (
    organiserFallback &&
    normalizeSlug(organiserFallback.slug) === normalizedCreatorSlug
  ) {
    return organiserFallback;
  }

  return {
    slug: normalizedCreatorSlug,
    first_name: organiserFallback?.first_name,
    last_name: organiserFallback?.last_name,
    email: organiserFallback?.email,
    id: organiserFallback?.id,
  };
}

/**
 * Every AFS participant of an appointment, creator included. The participant list saved with the
 * appointment wins; when it is empty (appointment saved without any AFS participant), the list is
 * rebuilt from the creator and the invited users.
 *
 * The creator is resolved here from the invited users and the organiser fallback only, never from
 * this list: the review page used to resolve it through its own `getCreatorUser()`, which called
 * back into this list, and an appointment without AFS participants crashed the page with
 * "Maximum call stack size exceeded".
 */
export function getAppointmentAfsParticipants(
  creatorSlug: string | null | undefined,
  savedParticipants: AfsUserChip[] | null | undefined,
  invitedParticipants: AfsUserChip[] | null | undefined,
  organiserFallback?: AfsUserChip | null
): AfsUserChip[] {
  const saved = savedParticipants || [];
  if (saved.length > 0) {
    return saved;
  }
  const invited = invitedParticipants || [];
  const creator = getAppointmentCreator(creatorSlug, invited, organiserFallback);
  if (!creator || invited.some(p => normalizeSlug(p.slug) === normalizeSlug(creator.slug))) {
    return invited;
  }
  return [creator, ...invited];
}

export function getInvitedAfsParticipants(
  creatorSlug: string | null | undefined,
  afsParticipants: AfsUserChip[] = []
): AfsUserChip[] {
  const normalizedCreatorSlug = normalizeSlug(creatorSlug);
  const seen = new Set<string>();
  const invited: AfsUserChip[] = [];

  for (const participant of afsParticipants) {
    const slug = normalizeSlug(participant.slug);
    if (!slug || slug === normalizedCreatorSlug || seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    invited.push(participant);
  }

  return invited;
}

export function getAfsUserDisplayName(user: AfsUserChip | null | undefined): string {
  if (!user) {
    return '';
  }
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName || user.slug || '';
}

export function getAppointmentRoleLabel(role: AppointmentAfsRole): string {
  return role === 'creator' ? 'Creator' : 'Invited';
}

export function getAppointmentRoleClass(role: AppointmentAfsRole): string {
  return role === 'creator'
    ? 'bg-light-primary text-primary'
    : 'bg-light-success text-success';
}

export type ApiUserAppointmentRole = 'creator' | 'participant';

export function getApiUserAppointmentRoleLabel(role?: string): string {
  if (role === 'creator') {
    return 'Creator';
  }
  if (role === 'participant') {
    return 'Invited';
  }
  return '';
}

export function getApiUserAppointmentRoleClass(role?: string): string {
  if (role === 'creator') {
    return 'bg-light-primary text-primary';
  }
  if (role === 'participant') {
    return 'bg-light-success text-success';
  }
  return '';
}
